/**
 * AppShell — Universal layout wrapper for publisher portal routes.
 *
 * Purpose: Provides sidebar navigation and responsive shell for all authenticated
 *          publisher pages (/dashboard, /publisher/titles, /settings, etc.).
 * Flow:
 *   - Desktop (≥1280px): Fixed sidebar (240px) + scrollable main area.
 *   - Tablet / Mobile (<1024px): Sidebar hidden → FloatingTopBar pill header +
 *     MobileNavPopup dropdown. Main area gets top padding to clear the bar.
 *
 * RTL-aware: sidebar auto-positions to inline-start; popup anchors to inline-end.
 *
 * NOTE: Auth is read via useAuthContext — no auth logic lives here.
 * AuthGuard components (GuestRoute, AuthRoute) wrap children via the route tree
 * so AppShell persists across navigations (stable element identity, only Outlet changes).
 */
import { Suspense, useState } from "react";
import { Outlet } from "react-router";
import { useTranslation } from "react-i18next";
import { Sidebar } from "@/shared/components/Sidebar";
import { PageSpinner } from "@/shared/components/PageSpinner";
import { ReadOnlyBanner } from "@/shared/components/ReadOnlyBanner";
import { FloatingTopBar } from "@/shared/components/FloatingTopBar";
import { MobileNavPopup } from "@/shared/components/MobileNavPopup";
import { useAuthContext } from "@/shared/hooks/useAuthContext";
import { useReadOnly } from "@/shared/hooks/useReadOnly";
import { useLogout } from "@/features/auth/hooks/useAuth";
import { useNotificationCenter } from "@/features/notifications/hooks/useNotifications";
// Direct module import (not the @/features/pricing barrel): importing the barrel
// here would statically pull the whole pricing feature (route pages, checkout)
// into AppShell's eager chunk, defeating router-level lazy code-splitting.
import { ComparePlansOverlay } from "@/features/pricing/components/ComparePlansOverlay";
import { DirectionProvider } from "@/app/DirectionProvider";
import {
  CreditCard,
  FileText,
  LayoutDashboard,
  Settings,
  Sparkles,
  type LucideIcon,
} from "lucide-react";

/* ── Publisher nav sections ──────────────────────────────────────── */

const PUBLISHER_NAV_SECTIONS: { label: string; items: { href: string; icon: LucideIcon; labelKey: string; exact?: boolean }[] }[] = [
  {
    label: "sidebar.sectionDashboard",
    items: [
      { href: "/dashboard",            icon: LayoutDashboard, labelKey: "sidebar.dashboard", exact: true },
      { href: "/publisher/titles",     icon: FileText,        labelKey: "sidebar.titles" },
      { href: "/services",             icon: Sparkles,        labelKey: "sidebar.services" },
    ],
  },
  {
    label: "sidebar.management",
    items: [
      { href: "/billing",            icon: CreditCard, labelKey: "sidebar.billing", exact: true },
      { href: "/dashboard/settings", icon: Settings,   labelKey: "sidebar.settings" },
    ],
  },
];

interface AppShellProps {
  children?: React.ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const auth = useAuthContext();
  const { mutate: logout } = useLogout();
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);

  const notificationCenter = useNotificationCenter({
    enabled: auth.status === "authenticated",
  });
  const showSidebar = auth.status === "authenticated";
  const readOnly = useReadOnly();

  const userName = auth.status === "authenticated"
    ? (auth.user.fullName || (auth.user.email.split("@")[0] ?? ""))
    : "";
  const userEmail = auth.status === "authenticated" ? auth.user.email : "";
  const userPhotoUrl = auth.status === "authenticated" ? auth.user.photoUrl : null;
  const unreadCount = notificationCenter.data?.unreadCount ?? 0;

  const handleSignOut = () => logout();

  return (
    <DirectionProvider>
      {/*
       * Shell layout:
       * - Mobile/tablet: block flow, full-width, body scrolls naturally
       * - Desktop (xl+): flex row, fixed height 100vh, inner areas scroll
       */}
      <div
        className="lg:flex lg:h-screen lg:overflow-hidden relative"
        style={{ background: "var(--admin-bg)" }}
      >
        {/* ── Desktop sidebar — hidden below xl ── */}
        {showSidebar && (
          <div className="hidden lg:flex">
            <Sidebar
              userName={userName}
              userEmail={userEmail}
              userPhotoUrl={userPhotoUrl}
              navSections={PUBLISHER_NAV_SECTIONS}
              onSignOut={handleSignOut}
              showBell={true}
              unreadNotificationCount={unreadCount}
              isAdmin={auth.isAdmin}
            />
          </div>
        )}

        {/* ── Mobile/tablet floating top bar ──
         * Owns its own frosted strip (blurred backdrop + fade) covering the pill
         * and the equal top/start/end gaps — see FloatingTopBar. */}
        {showSidebar && (
          <FloatingTopBar
            orgSubtitle={t("sidebar.publisherPanel")}
            onMenuClick={() => setMenuOpen((v) => !v)}
            menuOpen={menuOpen}
            unreadCount={unreadCount}
            notificationsHref="/notifications"
          />
        )}

        {/* ── Mobile/tablet popup nav ── */}
        {showSidebar && (
          <MobileNavPopup
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            userName={userName}
            userEmail={userEmail}
            userPhotoUrl={userPhotoUrl}
            navSections={PUBLISHER_NAV_SECTIONS}
            onSignOut={handleSignOut}
            isAdmin={auth.isAdmin}
          />
        )}

        {/* ── Main content area ── */}
        <div className="lg:flex-1 lg:overflow-y-auto">
          {/*
           * Top padding clears the floating bar on mobile/tablet, plus extra
           * breathing room below it. The bar's inset matches the page padding
           * (16px → 24px at sm); minimum clearance is top inset + 52px bar +
           * matching gap (84px mobile / 100px sm). We add ~12px on top of that:
           *   mobile: pt-24 = 96px
           *   sm:     pt-28 = 112px
           * sm:pt-* re-asserts it after sm:p-6 would otherwise override padding-top.
           * lg:pt-8 collapses top pad on desktop where the bar is hidden.
           * Kept in lockstep with AdminShell's main padding.
           */}
          <main className={showSidebar ? "p-4 pt-24 sm:p-6 sm:pt-28 lg:p-8 lg:pt-8" : "p-4 sm:p-6 lg:p-8"}>
            {/* Read-only banner: shown for an authenticated publisher whose
                subscription has lapsed (useReadOnly() === true). */}
            {showSidebar && readOnly && <ReadOnlyBanner />}
            {/* Shell-level Suspense: lazy route chunks load here so the sidebar
                stays mounted and only the content area shows the spinner. */}
            <Suspense fallback={<PageSpinner />}>{children ?? <Outlet />}</Suspense>
          </main>
        </div>

        {/* ── In-app plan-comparison overlay (URL-driven via ?compare=1) ──
         * Auth-gated: ComparePlansOverlay calls authenticated queries
         * (useSubscription), so it must NOT mount for guests (login/register). */}
        {showSidebar && <ComparePlansOverlay />}
      </div>
    </DirectionProvider>
  );
}
